// /src/hooks/useSupabaseProformaInvoices.ts
import { useState, useCallback } from 'react';
import { useSupabase } from '@/hooks/useSupabase';
import { 
  ProformaInvoice, 
  ProformaInvoiceFormData, 
  ProformaInvoiceFilters,
  ProformaInvoiceStatus,
  ProformaTemplate
} from '@/types/proformaInvoice';

// Funkcja pomocnicza do konwersji liczby na słowa
function numberToWords(amount: number): string {
  const units = ['', 'jeden', 'dwa', 'trzy', 'cztery', 'piec', 'szesc', 'siedem', 'osiem', 'dziewiec'];
  const teens = ['dziesiec', 'jedenascie', 'dwanascie', 'trzynascie', 'czternascie', 'pietnascie', 'szesnascie', 'siedemnascie', 'osiemnascie', 'dziewietnascie'];
  const tens = ['', '', 'dwadziescia', 'trzydziesci', 'czterdziesci', 'piecdziesiat', 'szescdziesiat', 'siedemdziesiat', 'osiemdziesiat', 'dziewiecdziesiat'];
  const hundreds = ['', 'sto', 'dwiescie', 'trzysta', 'czterysta', 'piecset', 'szescset', 'siedemset', 'osiemset', 'dziewiecset'];
  const thousands = ['', 'tysiac', 'dwa tysiace', 'trzy tysiace', 'cztery tysiace', 'piec tysiecy', 'szesc tysiecy', 'siedem tysiecy', 'osiem tysiecy', 'dziewiec tysiecy'];

  const zlote = Math.floor(amount);
  const grosze = Math.round((amount - zlote) * 100);

  function convertHundreds(n: number): string {
    if (n === 0) return '';
    if (n < 10) return units[n];
    if (n < 20) return teens[n - 10];
    if (n < 100) {
      const ten = Math.floor(n / 10);
      const unit = n % 10;
      return tens[ten] + (unit > 0 ? ' ' + units[unit] : '');
    }
    const hundred = Math.floor(n / 100);
    const rest = n % 100;
    return hundreds[hundred] + (rest > 0 ? ' ' + convertHundreds(rest) : '');
  }

  let words = '';
  
  if (zlote === 0) {
    words = 'zero';
  } else if (zlote < 1000) {
    words = convertHundreds(zlote);
  } else if (zlote < 10000) {
    const thousand = Math.floor(zlote / 1000);
    const rest = zlote % 1000;
    words = thousands[thousand] + (rest > 0 ? ' ' + convertHundreds(rest) : '');
  } else {
    const tenThousands = Math.floor(zlote / 1000);
    const rest = zlote % 1000;
    words = convertHundreds(tenThousands) + ' tysięcy' + (rest > 0 ? ' ' + convertHundreds(rest) : '');
  }

  words += ' zl';
  
  if (grosze > 0) {
    words += ' ' + grosze + '/100';
  }

  return words;
}

export const useSupabaseProformaInvoices = () => {
  const { supabase } = useSupabase();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pobierz faktury z filtrami
  const getInvoices = useCallback(async (filters?: ProformaInvoiceFilters) => {
    setLoading(true);
    setError(null);
    
    try {
      let query = supabase
        .from('proforma_invoices')
        .select(`
          *,
          proforma_invoice_items (
            id,
            description,
            quantity,
            unit,
            unit_price,
            vat_rate,
            net_amount,
            vat_amount,
            gross_amount,
            position
          )
        `)
        .order('created_at', { ascending: false });

      // Zastosuj filtry
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      
      if (filters?.startDate) {
        query = query.gte('issue_date', filters.startDate);
      }
      
      if (filters?.endDate) {
        query = query.lte('issue_date', filters.endDate);
      }
      
      if (filters?.searchTerm) {
        query = query.or(`
          invoice_number.ilike.%${filters.searchTerm}%,
          client_name.ilike.%${filters.searchTerm}%,
          client_nip.ilike.%${filters.searchTerm}%
        `);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      // Mapuj dane do formatu aplikacji
      const invoices: ProformaInvoice[] = (data || []).map(invoice => ({
        id: invoice.id,
        invoiceNumber: invoice.invoice_number,
        issueDate: invoice.issue_date,
        saleDate: invoice.sale_date,
        validUntil: invoice.valid_until || invoice.issue_date,
        paymentDeadline: invoice.payment_deadline,
        paymentMethod: invoice.payment_method,
        status: invoice.status as ProformaInvoiceStatus,
        
        // Dane klienta w nowym formacie
        clientName: invoice.client_name,
        clientAddress: invoice.client_address,
        clientPostalCode: invoice.client_postal_code,
        clientCity: invoice.client_city,
        clientNIP: invoice.client_nip,
        clientEmail: invoice.client_email,
        clientPhone: invoice.client_phone,
        
        // Dane dla kompatybilności wstecznej
        buyer: {
          name: invoice.client_name,
          address: invoice.client_address,
          postalCode: invoice.client_postal_code,
          city: invoice.client_city,
          nip: invoice.client_nip,
          email: invoice.client_email,
          phone: invoice.client_phone
        },
        
        // Pozycje w obu formatach
        items: (invoice.proforma_invoice_items || []).map((item: any) => ({
          id: item.id,
          name: item.description, // Dla kompatybilności
          description: item.description,
          quantity: item.quantity,
          unit: item.unit || 'szt.',
          netPrice: item.unit_price, // Dla kompatybilności
          unitPrice: item.unit_price,
          vatRate: item.vat_rate,
          netValue: item.net_amount, // Dla kompatybilności
          vatValue: item.vat_amount, // Dla kompatybilności
          grossValue: item.gross_amount, // Dla kompatybilności
          netAmount: item.net_amount,
          vatAmount: item.vat_amount,
          grossAmount: item.gross_amount
        })),
        
        // Podsumowanie w obu formatach
        totalNet: invoice.total_net || 0,
        totalVat: invoice.total_vat || 0,
        totalGross: invoice.total_gross || 0,
        
        summary: {
          netTotal: invoice.total_net || 0,
          vatTotal: invoice.total_vat || 0,
          grossTotal: invoice.total_gross || 0,
          grossTotalInWords: numberToWords(invoice.total_gross || 0)
        },
        
        notes: invoice.notes,
        internalNotes: invoice.internal_notes,
        createdAt: invoice.created_at,
        updatedAt: invoice.updated_at,
        createdBy: invoice.created_by,
        pdfUrl: invoice.pdf_url,
        emailSentAt: invoice.email_sent_at,
        qualityControlId: invoice.quality_control_id
      }));

      return { data: invoices, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd pobierania faktur';
      setError(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Pobierz pojedynczą fakturę
  const getInvoice = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await supabase
        .from('proforma_invoices')
        .select(`
          *,
          proforma_invoice_items (
            id,
            description,
            quantity,
            unit_price,
            vat_rate,
            net_amount,
            vat_amount,
            gross_amount,
            position
          ),
          proforma_invoice_history (
            id,
            action,
            changes,
            created_at,
            created_by
          )
        `)
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;

      // Mapuj dane do formatu aplikacji
      const mappedInvoice: ProformaInvoice = {
        id: data.id,
        invoiceNumber: data.invoice_number,
        issueDate: data.issue_date,
        saleDate: data.sale_date,
        validUntil: data.valid_until || data.issue_date,
        paymentDeadline: data.payment_deadline,
        paymentMethod: data.payment_method,
        status: data.status as ProformaInvoiceStatus,
        
        // Dane klienta w nowym formacie
        clientName: data.client_name,
        clientAddress: data.client_address,
        clientPostalCode: data.client_postal_code,
        clientCity: data.client_city,
        clientNIP: data.client_nip,
        clientEmail: data.client_email,
        clientPhone: data.client_phone,
        
        // Dane dla kompatybilności wstecznej
        buyer: {
          name: data.client_name,
          address: data.client_address,
          postalCode: data.client_postal_code,
          city: data.client_city,
          nip: data.client_nip,
          email: data.client_email,
          phone: data.client_phone
        },
        
        // Pozycje w obu formatach
        items: (data.proforma_invoice_items || []).map((item: any) => ({
          id: item.id,
          name: item.description, // Dla kompatybilności
          description: item.description,
          quantity: item.quantity,
          unit: item.unit || 'szt.',
          netPrice: item.unit_price, // Dla kompatybilności
          unitPrice: item.unit_price,
          vatRate: item.vat_rate,
          netValue: item.net_amount, // Dla kompatybilności
          vatValue: item.vat_amount, // Dla kompatybilności
          grossValue: item.gross_amount, // Dla kompatybilności
          netAmount: item.net_amount,
          vatAmount: item.vat_amount,
          grossAmount: item.gross_amount
        })),
        
        // Podsumowanie w obu formatach
        totalNet: data.total_net || 0,
        totalVat: data.total_vat || 0,
        totalGross: data.total_gross || 0,
        
        summary: {
          netTotal: data.total_net || 0,
          vatTotal: data.total_vat || 0,
          grossTotal: data.total_gross || 0,
          grossTotalInWords: numberToWords(data.total_gross || 0)
        },
        
        notes: data.notes,
        internalNotes: data.internal_notes,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        createdBy: data.created_by,
        pdfUrl: data.pdf_url,
        emailSentAt: data.email_sent_at,
        qualityControlId: data.quality_control_id
      };

      return { data: mappedInvoice, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd pobierania faktury';
      setError(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Generuj numer faktury
  const generateInvoiceNumber = useCallback(async (): Promise<string> => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    
    try {
      // Pobierz ostatni numer z tego miesiąca
      const { data, error } = await supabase
        .from('proforma_invoices')
        .select('invoice_number')
        .like('invoice_number', `FP/${year}/${month}/%`)
        .order('invoice_number', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (!data || data.length === 0) {
        return `FP/${year}/${month}/001`;
      }

      // Wyciągnij numer i zwiększ o 1
      const lastNumber = data[0].invoice_number;
      const lastSequence = parseInt(lastNumber.split('/').pop() || '0');
      const newSequence = String(lastSequence + 1).padStart(3, '0');

      return `FP/${year}/${month}/${newSequence}`;
    } catch (err) {
      console.error('Error generating invoice number:', err);
      return `FP/${year}/${month}/001`;
    }
  }, [supabase]);

  // Utwórz fakturę
  const createInvoice = useCallback(async (invoiceData: ProformaInvoiceFormData) => {
    setLoading(true);
    setError(null);
    
    console.log('=== TWORZENIE FAKTURY ===');
    console.log('Dane wejściowe:', invoiceData);
    
    try {
      // Wygeneruj numer faktury
      const invoiceNumber = await generateInvoiceNumber();
      console.log('Wygenerowany numer:', invoiceNumber);
      
      // Oblicz sumy
      let totalNet = 0;
      let totalVat = 0;
      let totalGross = 0;
      
      const calculatedItems = invoiceData.items.map(item => {
        const netAmount = item.quantity * item.unitPrice;
        const vatAmount = item.vatRate === -1 ? 0 : (netAmount * item.vatRate) / 100;
        const grossAmount = netAmount + vatAmount;
        
        totalNet += netAmount;
        totalVat += vatAmount;
        totalGross += grossAmount;
        
        return {
          ...item,
          net_amount: netAmount,
          vat_amount: vatAmount,
          gross_amount: grossAmount
        };
      });
      
      // Dane do wysłania
      const invoiceToSave = {
        invoice_number: invoiceNumber,
        issue_date: invoiceData.issueDate,
        sale_date: invoiceData.saleDate,
        payment_deadline: invoiceData.paymentDeadline,
        payment_method: invoiceData.paymentMethod,
        status: 'draft',
        client_name: invoiceData.clientName,
        client_address: invoiceData.clientAddress,
        client_postal_code: invoiceData.clientPostalCode,
        client_city: invoiceData.clientCity,
        client_nip: invoiceData.clientNIP,
        client_email: invoiceData.clientEmail,
        client_phone: invoiceData.clientPhone,
        total_net: totalNet,
        total_vat: totalVat,
        total_gross: totalGross,
        notes: invoiceData.notes,
        internal_notes: invoiceData.internalNotes,
        created_by: 'user', // Tymczasowo, ponieważ nie używamy systemu autoryzacji
        quality_control_id: invoiceData.qualityControlId
      };
      
      console.log('Dane do zapisania:', invoiceToSave);
      
      // Rozpocznij transakcję
      const { data: invoice, error: invoiceError } = await supabase
        .from('proforma_invoices')
        .insert(invoiceToSave)
        .select()
        .single();

      console.log('Odpowiedź z Supabase:', { data: invoice, error: invoiceError });

      if (invoiceError) {
        console.error('Błąd z Supabase:', invoiceError);
        console.error('Szczegóły błędu:', {
          message: invoiceError.message,
          details: invoiceError.details,
          hint: invoiceError.hint,
          code: invoiceError.code
        });
        throw invoiceError;
      }

      // Dodaj pozycje faktury
      const items = calculatedItems.map((item, index) => ({
        invoice_id: invoice.id,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit || 'szt.', // Dodaj unit
        unit_price: item.unitPrice,
        vat_rate: item.vatRate,
        net_amount: item.net_amount,
        vat_amount: item.vat_amount,
        gross_amount: item.gross_amount,
        position: index + 1
      }));

      console.log('Pozycje do zapisania:', items);

      const { error: itemsError } = await supabase
        .from('proforma_invoice_items')
        .insert(items);

      if (itemsError) {
        // Rollback - usuń fakturę jeśli nie udało się dodać pozycji
        await supabase
          .from('proforma_invoices')
          .delete()
          .eq('id', invoice.id);
        throw itemsError;
      }

      // Pobierz kompletną fakturę z pozycjami
      const { data: completeInvoice } = await getInvoice(invoice.id);

      return { data: completeInvoice, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd tworzenia faktury';
      setError(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [supabase, getInvoice, generateInvoiceNumber]);

  // Aktualizuj fakturę
  const updateInvoice = useCallback(async (id: string, updates: Partial<ProformaInvoiceFormData>) => {
    setLoading(true);
    setError(null);
    
    try {
      // Aktualizuj główne dane faktury
      const invoiceUpdates: any = {};
      
      if (updates.issueDate) invoiceUpdates.issue_date = updates.issueDate;
      if (updates.saleDate) invoiceUpdates.sale_date = updates.saleDate;
      if (updates.paymentDeadline) invoiceUpdates.payment_deadline = updates.paymentDeadline;
      if (updates.paymentMethod) invoiceUpdates.payment_method = updates.paymentMethod;
      if (updates.clientName) invoiceUpdates.client_name = updates.clientName;
      if (updates.clientAddress) invoiceUpdates.client_address = updates.clientAddress;
      if (updates.clientPostalCode) invoiceUpdates.client_postal_code = updates.clientPostalCode;
      if (updates.clientCity) invoiceUpdates.client_city = updates.clientCity;
      if (updates.clientNIP) invoiceUpdates.client_nip = updates.clientNIP;
      if (updates.clientEmail) invoiceUpdates.client_email = updates.clientEmail;
      if (updates.clientPhone) invoiceUpdates.client_phone = updates.clientPhone;
      if (updates.notes !== undefined) invoiceUpdates.notes = updates.notes;
      if (updates.internalNotes !== undefined) invoiceUpdates.internal_notes = updates.internalNotes;

      const { error: updateError } = await supabase
        .from('proforma_invoices')
        .update(invoiceUpdates)
        .eq('id', id);

      if (updateError) throw updateError;

      // Jeśli są zmiany w pozycjach
      if (updates.items) {
        // Usuń stare pozycje
        const { error: deleteError } = await supabase
          .from('proforma_invoice_items')
          .delete()
          .eq('invoice_id', id);

        if (deleteError) throw deleteError;

        // Dodaj nowe pozycje
        const items = updates.items.map((item, index) => ({
          invoice_id: id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          vat_rate: item.vatRate,
          position: index + 1
        }));

        const { error: itemsError } = await supabase
          .from('proforma_invoice_items')
          .insert(items);

        if (itemsError) throw itemsError;
      }

      // Pobierz zaktualizowaną fakturę
      const { data: updatedInvoice } = await getInvoice(id);

      return { data: updatedInvoice, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd aktualizacji faktury';
      setError(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [supabase, getInvoice]);

  // Usuń fakturę
  const deleteInvoice = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { error: deleteError } = await supabase
        .from('proforma_invoices')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      return { error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd usuwania faktury';
      setError(errorMessage);
      return { error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Zmień status faktury
  const updateInvoiceStatus = useCallback(async (id: string, status: ProformaInvoiceStatus) => {
    setLoading(true);
    setError(null);
    
    try {
      const { error: updateError } = await supabase
        .from('proforma_invoices')
        .update({ status })
        .eq('id', id);

      if (updateError) throw updateError;

      return { error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd zmiany statusu';
      setError(errorMessage);
      return { error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Zapisz URL PDF
  const updatePdfUrl = useCallback(async (id: string, pdfUrl: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { error: updateError } = await supabase
        .from('proforma_invoices')
        .update({ pdf_url: pdfUrl })
        .eq('id', id);

      if (updateError) throw updateError;

      return { error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd zapisywania PDF';
      setError(errorMessage);
      return { error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Dodaj do kolejki email
  const queueEmail = useCallback(async (invoiceId: string, recipientEmail: string, subject: string, body: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { error: queueError } = await supabase
        .from('proforma_email_queue')
        .insert({
          invoice_id: invoiceId,
          recipient_email: recipientEmail,
          subject,
          body,
          created_by: 'user' // Tymczasowo, ponieważ nie używamy systemu autoryzacji
        });

      if (queueError) throw queueError;

      // Oznacz fakturę jako wysłaną emailem
      await supabase
        .from('proforma_invoices')
        .update({ email_sent_at: new Date().toISOString() })
        .eq('id', invoiceId);

      return { error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd dodawania do kolejki email';
      setError(errorMessage);
      return { error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Pobierz szablony
  const getTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await supabase
        .from('proforma_templates')
        .select(`
          *,
          proforma_template_items (
            id,
            description,
            quantity,
            unit_price,
            vat_rate,
            position
          )
        `)
        .eq('is_active', true)
        .order('name');

      if (fetchError) throw fetchError;

      const templates: ProformaTemplate[] = (data || []).map(template => ({
        id: template.id,
        name: template.name,
        description: template.description,
        clientName: template.client_name,
        clientAddress: template.client_address,
        clientPostalCode: template.client_postal_code,
        clientCity: template.client_city,
        clientNIP: template.client_nip,
        clientEmail: template.client_email,
        clientPhone: template.client_phone,
        paymentMethod: template.payment_method,
        paymentDays: template.payment_days,
        notes: template.notes,
        items: template.proforma_template_items.map((item: any) => ({
          id: item.id,
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unit_price,
          vatRate: item.vat_rate
        })),
        isActive: template.is_active,
        createdAt: template.created_at,
        updatedAt: template.updated_at
      }));

      return { data: templates, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd pobierania szablonów';
      setError(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Utwórz fakturę z szablonu
  const createFromTemplate = useCallback(async (templateId: string, qualityControlId?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: rpcError } = await supabase
        .rpc('create_invoice_from_template', {
          p_template_id: templateId,
          p_quality_control_id: qualityControlId,
          p_created_by: 'user' // Tymczasowo, ponieważ nie używamy systemu autoryzacji
        });

      if (rpcError) throw rpcError;

      return { data, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd tworzenia faktury z szablonu';
      setError(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Eksportuj faktury do CSV
  const exportToCSV = useCallback(async (filters?: ProformaInvoiceFilters) => {
    const { data: invoices } = await getInvoices(filters);
    
    if (!invoices || invoices.length === 0) {
      return { error: 'Brak faktur do eksportu' };
    }

    try {
      const headers = [
        'Numer faktury',
        'Data wystawienia',
        'Data sprzedaży',
        'Termin płatności',
        'Status',
        'Klient',
        'NIP',
        'Adres',
        'Kwota netto',
        'VAT',
        'Kwota brutto',
        'Metoda płatności',
        'Email wysłany'
      ];

      const rows = invoices.map(invoice => [
        invoice.invoiceNumber,
        invoice.issueDate,
        invoice.saleDate,
        invoice.paymentDeadline,
        invoice.status,
        invoice.clientName,
        invoice.clientNIP,
        `${invoice.clientAddress}, ${invoice.clientPostalCode} ${invoice.clientCity}`,
        invoice.totalNet.toFixed(2),
        invoice.totalVat.toFixed(2),
        invoice.totalGross.toFixed(2),
        invoice.paymentMethod,
        invoice.emailSentAt ? 'Tak' : 'Nie'
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `faktury-proforma-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return { error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd eksportu do CSV';
      return { error: errorMessage };
    }
  }, [getInvoices]);

  // Pobierz historię zmian
  const getInvoiceHistory = useCallback(async (invoiceId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await supabase
        .from('proforma_invoice_history')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      return { data, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd pobierania historii';
      setError(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Statystyki
  const getStatistics = useCallback(async (startDate?: string, endDate?: string) => {
    setLoading(true);
    setError(null);
    
    try {
      let query = supabase
        .from('proforma_invoices')
        .select('status, total_net, total_vat, total_gross, created_at');

      if (startDate) {
        query = query.gte('created_at', startDate);
      }
      
      if (endDate) {
        query = query.lte('created_at', endDate);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      const stats = {
        total: data?.length || 0,
        byStatus: {
          draft: 0,
          sent: 0,
          paid: 0,
          cancelled: 0,
          overdue: 0
        },
        totalValue: {
          net: 0,
          vat: 0,
          gross: 0
        }
      };

      data?.forEach(invoice => {
        stats.byStatus[invoice.status as ProformaInvoiceStatus]++;
        stats.totalValue.net += invoice.total_net;
        stats.totalValue.vat += invoice.total_vat;
        stats.totalValue.gross += invoice.total_gross;
      });

      return { data: stats, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd pobierania statystyk';
      setError(errorMessage);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  return {
    // Stan
    loading,
    error,
    
    // Metody CRUD
    getInvoices,
    getInvoice,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    
    // Metody pomocnicze
    updateInvoiceStatus,
    updatePdfUrl,
    queueEmail,
    
    // Szablony
    getTemplates,
    createFromTemplate,
    
    // Eksport i historia
    exportToCSV,
    getInvoiceHistory,
    
    // Statystyki
    getStatistics
  };
};
